import * as React from "react";
import {
  Checkbox,
  TextBox,
  NumericTextBox,
  NumericTextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  DropDownList,
  ListItemProps,
  MultiSelect,
  MultiSelectFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { DatePicker, DateRangePicker } from "@progress/kendo-react-dateinputs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import CPButton from "shared/components/shared/CPButton/CPButton";
import {
  CPGridV2FilterDropdownOption,
  CPGridV2FilterOption,
  CPGridV2SavedFilter,
} from "../types";
import "./CPGridV2FiltersStyles.scss";

/* Normalises the options array for a dropdown filter into { label, value } objects.
   Accepts either plain strings or objects that already have the right shape. */
const normaliseDropdownOptions = (
  options: CPGridV2FilterDropdownOption[],
): { label: string; value: unknown }[] =>
  options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  );

// Placeholder item shown at the top of every single-select dropdown.
const DROPDOWN_DEFAULT_ITEM = { label: "— Select —", value: null };

/* A dropdown where each item is a checkbox. Extracted as its own component
   so useState and useRef hooks are valid inside it. */
interface MultiCheckFilterProps {
  data: { label: string; value: unknown }[];
  selectedVals: unknown[];
  placeholder: string;
  defaultAllOptions?: boolean;
  showSelectAllControls?: boolean;
  onChange: (vals: unknown[]) => void;
}

const MultiCheckFilter: React.FC<MultiCheckFilterProps> = ({
  data,
  selectedVals,
  placeholder,
  defaultAllOptions = false,
  showSelectAllControls = false,
  onChange,
}) => {
  const [filterText, setFilterText] = React.useState("");
  const [opened, setOpened] = React.useState(false);
  const lastDefaultedValuesRef = React.useRef<unknown[] | null>(null);
  const userChangedRef = React.useRef(false);
  // keeps the popup open when focus moves to the search input
  const searchFocused = React.useRef(false);

  React.useEffect(() => {
    if (!defaultAllOptions || data.length === 0) return;

    const values = data.map((item) => item.value);
    const previousValues = lastDefaultedValuesRef.current;
    const optionsChanged =
      previousValues == null ||
      values.length !== previousValues.length ||
      values.some((value, index) => !Object.is(value, previousValues[index]));

    if (!optionsChanged) return;
    lastDefaultedValuesRef.current = values;
    if (!userChangedRef.current) onChange(values);
  }, [data, defaultAllOptions, onChange]);

  const handleChange = (values: unknown[]) => {
    userChangedRef.current = true;
    onChange(values);
  };

  const filteredData = filterText
    ? data.filter((d) =>
        d.label.toLowerCase().includes(filterText.toLowerCase()),
      )
    : data;

  const selected = data.filter((d) => selectedVals.includes(d.value));

  // Show individual tags for up to 3 selections; beyond that collapse into a count tag.
  const tags =
    selected.length === 0
      ? []
      : selected.length <= 3
        ? undefined
        : [{ text: `${selected.length} selected`, data: selected }];

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps,
  ) => (
    <li
      onClick={(e) => itemProps.onClick(itemProps.index, e)}
      className="cpgv2-filter-multicheck-item"
    >
      <Checkbox
        checked={selectedVals.includes(itemProps.dataItem.value)}
        label={itemProps.dataItem.label}
      />
    </li>
  );

  // delay close so the search input has time to receive focus before we decide whether to close
  const handleClose = React.useCallback(() => {
    setTimeout(() => {
      if (!searchFocused.current) {
        setOpened(false);
        setFilterText("");
      }
    }, 50);
  }, []);

  const header = (
    <div className="cpgv2-filter-multicheck-search">
      <TextBox
        value={filterText}
        placeholder={placeholder}
        autoComplete="off"
        size="small"
        onChange={(e) => setFilterText((e.value as string) ?? "")}
        onFocus={() => {
          searchFocused.current = true;
        }}
        onBlur={() => {
          searchFocused.current = false;
        }}
      />
      {showSelectAllControls && (
        <div className="cpgv2-filter-multicheck-actions">
          <Button
            type="button"
            fillMode="flat"
            themeColor="primary"
            size="small"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleChange(data.map((item) => item.value))}
          >
            Select All
          </Button>
          <Button
            type="button"
            fillMode="flat"
            themeColor="primary"
            size="small"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => handleChange([])}
          >
            Unselect All
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <MultiSelect
      data={filteredData}
      value={selected}
      dataItemKey="value"
      textField="label"
      autoClose={false}
      opened={opened}
      onOpen={() => setOpened(true)}
      onClose={handleClose}
      itemRender={itemRender}
      tags={tags}
      header={header}
      placeholder={placeholder}
      onChange={(e) =>
        handleChange((e.target.value ?? []).map((v: any) => v.value))
      }
    />
  );
};

// Union of every value shape a filter input can produce.
type FilterValue =
  | string
  | number
  | null
  | Date
  | { start: Date | null; end: Date | null }
  | unknown[]
  | undefined;

interface CPGridV2FiltersProps {
  /** Filter definitions — one entry per visible filter input. */
  filterOptions: CPGridV2FilterOption[];
  /** Called on Apply with only the non-empty values keyed by columnKey. */
  onApply: (filters: Record<string, unknown>) => void;
  /** Called on Clear after internal state has been reset. */
  onClear: () => void;
  /** Fires whenever the number of active filters changes, including on clear. */
  onActiveCountChange?: (count: number) => void;
  /** Pre-loaded saved filters shown in the Load dropdown at the top of the panel. */
  savedFilters?: CPGridV2SavedFilter[];
  /** Called on Save with the name the user typed and the current non-empty filter values. */
  onSaveFilter?: (name: string, filters: Record<string, unknown>) => void;
  /** Shows the Save / Load section when true. */
  showSaveFilters?: boolean;
}

// Placeholder shown in the saved filters dropdown before the user picks one.
const SAVED_FILTER_DEFAULT_ITEM: CPGridV2SavedFilter = {
  id: -1,
  name: "— Select a saved filter —",
  filters: {},
};

/**
 * Renders filter inputs for each CPGridV2FilterOption inside the drawer panel.
 * Maintains its own form state and only notifies the parent on Apply or Clear.
 * onApply receives a flat Record<string, unknown> ready to send as a query payload.
 *
 * @example
 * <CPGridV2Filters
 *   filterOptions={[
 *     { filterType: "text", label: "Username", columnKey: "username" },
 *     { filterType: "dropdown", label: "Status", columnKey: "status",
 *       options: [{ label: "Active", value: 1 }] },
 *   ]}
 *   onApply={(filters) => fetchUsers(filters)}
 *   onClear={() => fetchUsers({})}
 * />
 */
const CPGridV2Filters: React.FC<CPGridV2FiltersProps> = ({
  filterOptions,
  onApply,
  onClear,
  onActiveCountChange,
  savedFilters,
  onSaveFilter,
  showSaveFilters = false,
}) => {
  // Holds option lists that were loaded asynchronously via optionsFn.
  const [resolvedOptions, setResolvedOptions] = React.useState<
    Record<string, { label: string; value: unknown }[]>
  >({});

  React.useEffect(() => {
    let cancelled = false;
    const dynamicFilters = filterOptions.filter((o) => o.optionsFn != null);
    if (dynamicFilters.length === 0) return;

    Promise.all(
      dynamicFilters.map(async (o) => {
        const raw = await o.optionsFn!();
        return { columnKey: o.columnKey, data: normaliseDropdownOptions(raw) };
      }),
    ).then((results) => {
      if (cancelled) return;
      setResolvedOptions((prev) => {
        const next = { ...prev };
        results.forEach(({ columnKey, data }) => {
          next[columnKey] = data;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [filterOptions]);

  // Current value for each filter input, keyed by columnKey.
  const [filterValues, setFilterValues] = React.useState<
    Record<string, FilterValue>
  >(() =>
    filterOptions.reduce<Record<string, FilterValue>>((acc, opt) => {
      if (opt.defaultValue !== undefined) {
        acc[opt.columnKey] = opt.defaultValue as FilterValue;
      }
      return acc;
    }, {}),
  );

  const [selectedSavedFilter, setSelectedSavedFilter] =
    React.useState<CPGridV2SavedFilter | null>(null);
  const [saveName, setSaveName] = React.useState("");

  // Tracks the search text typed inside each autocomplete input, keyed by columnKey.
  const [acFilterText, setAcFilterText] = React.useState<
    Record<string, string>
  >({});

  const setAcFilter = (columnKey: string, text: string) =>
    setAcFilterText((prev) => ({ ...prev, [columnKey]: text }));

  // notify parent of any active filters coming from defaultValues
  React.useEffect(() => {
    const count = filterOptions.filter(
      (opt) =>
        opt.defaultAllOptions ||
        (opt.defaultValue !== undefined &&
          opt.defaultValue !== null &&
          opt.defaultValue !== ""),
    ).length;
    if (count > 0) onActiveCountChange?.(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (columnKey: string, value: FilterValue) =>
    setFilterValues((prev) => ({ ...prev, [columnKey]: value }));

  /* A select-all style filter with every option checked restricts nothing,
     so it is omitted (also avoids huge query strings, e.g. thousands of agencies). */
  const isAllSelected = (columnKey: string, val: unknown[]) => {
    const option = filterOptions.find((o) => o.columnKey === columnKey);
    return (
      option?.filterType === "multicheck" &&
      option.showSelectAllControls === true &&
      (option.options?.length ?? 0) > 0 &&
      val.length >= (option.options?.length ?? 0)
    );
  };

  /* Builds a clean payload from the current filter state and sends it to the parent.
     Empty values (null, "", empty arrays, daterange with no dates) are excluded. */
  const handleApply = () => {
    const payload = Object.entries(filterValues).reduce<
      Record<string, unknown>
    >((acc, [key, val]) => {
      if (val != null && val !== "") {
        if (Array.isArray(val) && val.length === 0) return acc;
        if (Array.isArray(val) && isAllSelected(key, val)) return acc;
        // skip daterange if both dates are empty
        if (
          typeof val === "object" &&
          !Array.isArray(val) &&
          "start" in val &&
          val.start == null &&
          val.end == null
        ) {
          return acc;
        }
        if (Array.isArray(val)) {
          const option = filterOptions.find((o) => o.columnKey === key);
          if (option?.toType === "array") {
            acc[key] = val;
          } else {
            acc[key] = val.join(",");
          }
        } else {
          acc[key] = val;
        }
      }
      return acc;
    }, {});

    onActiveCountChange?.(Object.keys(payload).length);
    onApply(payload);
  };

  // Populates all filter inputs from the selected saved filter.
  const handleLoadSavedFilter = (e: any) => {
    const val: CPGridV2SavedFilter | null =
      e.value?.id == null ? null : e.value;
    setSelectedSavedFilter(val);
    if (val != null) {
      setFilterValues(val.filters as Record<string, FilterValue>);
      setSaveName(val.name);
    }
  };

  // Serialises the current filters the same way handleApply does, then passes them up.
  const handleSaveFilter = () => {
    const name = saveName.trim();
    if (!name) return;
    const payload = Object.entries(filterValues).reduce<
      Record<string, unknown>
    >((acc, [key, val]) => {
      if (val != null && val !== "") {
        if (Array.isArray(val) && val.length === 0) return acc;
        if (
          typeof val === "object" &&
          !Array.isArray(val) &&
          "start" in val &&
          val.start == null &&
          val.end == null
        ) {
          return acc;
        }
        if (Array.isArray(val)) {
          const option = filterOptions.find((o) => o.columnKey === key);
          if (option?.toType === "array") {
            acc[key] = val;
          } else {
            acc[key] = val.join(",");
          }
        } else {
          acc[key] = val;
        }
      }
      return acc;
    }, {});
    onSaveFilter?.(name, payload);
  };

  const handleClear = () => {
    setFilterValues({});
    setSelectedSavedFilter(null);
    setSaveName("");
    onActiveCountChange?.(0);
    onClear();
  };

  // Returns the right input control for a given filter definition.
  const renderInput = (option: CPGridV2FilterOption) => {
    const rawValue = filterValues[option.columnKey];

    switch (option.filterType) {
      case "number":
        return (
          <NumericTextBox
            value={(rawValue as number | null | undefined) ?? null}
            placeholder={option.placeholder}
            onChange={(e: NumericTextBoxChangeEvent) =>
              set(option.columnKey, e.value ?? null)
            }
          />
        );

      case "dropdown": {
        const data =
          resolvedOptions[option.columnKey] ??
          normaliseDropdownOptions(option.options ?? []);
        const selected =
          data.find((d) => d.value === rawValue) ?? DROPDOWN_DEFAULT_ITEM;

        return (
          <DropDownList
            data={data}
            value={selected}
            dataItemKey="value"
            textField="label"
            defaultItem={DROPDOWN_DEFAULT_ITEM}
            onChange={(e) =>
              set(option.columnKey, e.target.value?.value ?? null)
            }
          />
        );
      }

      case "date":
        return (
          <DatePicker
            value={(rawValue as Date | null | undefined) ?? null}
            onChange={(e) => set(option.columnKey, e.value ?? null)}
          />
        );

      case "daterange":
        return (
          <DateRangePicker
            value={
              (rawValue as { start: Date | null; end: Date | null }) ?? {
                start: null,
                end: null,
              }
            }
            onChange={(e) =>
              set(option.columnKey, {
                start: e.value?.start ?? null,
                end: e.value?.end ?? null,
              })
            }
          />
        );

      case "email":
        return (
          <TextBox
            type="email"
            value={(rawValue as string | undefined) ?? ""}
            placeholder={option.placeholder ?? "Enter email…"}
            onChange={(e) => set(option.columnKey, e.value as string)}
          />
        );

      case "phone":
        return (
          <TextBox
            type="tel"
            value={(rawValue as string | undefined) ?? ""}
            placeholder={option.placeholder ?? "Enter phone…"}
            onChange={(e) => set(option.columnKey, e.value as string)}
          />
        );

      case "autocomplete-single": {
        const data =
          resolvedOptions[option.columnKey] ??
          normaliseDropdownOptions(option.options ?? []);
        const filterText = acFilterText[option.columnKey] ?? "";
        const filteredData = filterText
          ? data.filter((d) =>
              d.label.toLowerCase().includes(filterText.toLowerCase()),
            )
          : data;
        const selected = data.find((d) => d.value === rawValue) ?? null;

        return (
          <ComboBox
            data={filteredData}
            value={selected}
            dataItemKey="value"
            textField="label"
            filterable
            clearButton
            suggest
            placeholder={
              option.placeholder ?? `Search ${option.label.toLowerCase()}…`
            }
            onFilterChange={(e: ComboBoxFilterChangeEvent) =>
              setAcFilter(option.columnKey, e.filter.value)
            }
            onChange={(e) =>
              set(option.columnKey, e.target.value?.value ?? null)
            }
          />
        );
      }

      case "autocomplete-multi": {
        const data =
          resolvedOptions[option.columnKey] ??
          normaliseDropdownOptions(option.options ?? []);
        const filterText = acFilterText[option.columnKey] ?? "";
        const filteredData = filterText
          ? data.filter((d) =>
              d.label.toLowerCase().includes(filterText.toLowerCase()),
            )
          : data;
        const selectedVals = (rawValue as unknown[] | undefined) ?? [];
        const selected = data.filter((d) => selectedVals.includes(d.value));

        return (
          <MultiSelect
            data={filteredData}
            value={selected}
            dataItemKey="value"
            textField="label"
            filterable
            placeholder={
              option.placeholder ?? `Search ${option.label.toLowerCase()}…`
            }
            onFilterChange={(e: MultiSelectFilterChangeEvent) =>
              setAcFilter(option.columnKey, e.filter.value)
            }
            onChange={(e) =>
              set(
                option.columnKey,
                (e.target.value ?? []).map((v: any) => v.value),
              )
            }
          />
        );
      }

      case "multicheck": {
        const data =
          resolvedOptions[option.columnKey] ??
          normaliseDropdownOptions(option.options ?? []);
        const selectedVals = (rawValue as unknown[] | undefined) ?? [];

        return (
          <MultiCheckFilter
            data={data}
            selectedVals={selectedVals}
            defaultAllOptions={option.defaultAllOptions}
            showSelectAllControls={option.showSelectAllControls}
            placeholder={
              option.placeholder ?? `Select ${option.label.toLowerCase()}…`
            }
            onChange={(vals) => set(option.columnKey, vals)}
          />
        );
      }

      default:
        return (
          <TextBox
            value={(rawValue as string | undefined) ?? ""}
            placeholder={
              option.placeholder ?? `Filter by ${option.label.toLowerCase()}…`
            }
            onChange={(e) => set(option.columnKey, e.value as string)}
          />
        );
    }
  };

  return (
    <div className="cpgv2-filters">
      {showSaveFilters && (
        <div className="cpgv2-filters-load">
          <Label className="cpgv2-filter-label">Load Saved Filter</Label>
          <DropDownList
            data={savedFilters ?? []}
            value={selectedSavedFilter}
            dataItemKey="id"
            textField="name"
            defaultItem={{
              id: null,
              name: "\u2014 Select a filter \u2014",
              filters: {},
            }}
            onChange={handleLoadSavedFilter}
          />
        </div>
      )}

      <div className="cpgv2-filters-title-bar">
        <span className="cpgv2-filters-title-bar__text">Filter By</span>
      </div>

      <div className="cpgv2-filters-inputs">
        {filterOptions.map((option) => (
          <div key={option.columnKey} className="cpgv2-filter-item">
            <Label className="cpgv2-filter-label">
              {option.label}
              {option.required && (
                <span className="cpgv2-filter-required" aria-hidden="true">
                  {" "}
                  *
                </span>
              )}
            </Label>
            {renderInput(option)}
          </div>
        ))}
      </div>

      <div className="cpgv2-filters-actions">
        <CPButton
          themeColor="primary"
          fillMode="solid"
          size="small"
          onClick={handleApply}
        >
          Apply
        </CPButton>
        <CPButton
          themeColor="base"
          fillMode="outline"
          size="small"
          onClick={handleClear}
        >
          Clear
        </CPButton>
      </div>

      {showSaveFilters && (
        <div className="cpgv2-filters-save-section">
          <div className="cpgv2-filters-save-row">
            <TextBox
              placeholder="Name this filter…"
              value={saveName}
              onChange={(e) => setSaveName(e.value as string)}
            />
            <CPButton
              themeColor="primary"
              fillMode="solid"
              size="small"
              disabled={!saveName.trim()}
              onClick={handleSaveFilter}
            >
              Save
            </CPButton>
          </div>
          <span className="cpgv2-filters-save-hint">
            {saveName.trim()
              ? (savedFilters ?? []).some((f) => f.name === saveName.trim())
                ? `Will update "${saveName.trim()}"`
                : `Will save as "${saveName.trim()}"`
              : "Enter a name above to save the current filters"}
          </span>
        </div>
      )}
    </div>
  );
};

export default CPGridV2Filters;
